import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function PartnerOnboarding() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleConnect = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch('/api/create-connect-account', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Fehler beim Verbinden mit Stripe');
            }

            // Redirect to Stripe Onboarding
            window.location.href = data.url;
        } catch (err: any) {
            setError(err.message);
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pt-32 pb-20 flex items-center justify-center px-4">
            <div className="max-w-md w-full">

                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-tron-primary/10 border border-tron-primary/20 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-sm font-semibold mb-6 shadow-sm"
                    >
                        <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#1E3A8A] animate-pulse" />
                        Partner Portal
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-4 tracking-tight"
                    >
                        Werde Reputrust <span className="text-transparent bg-clip-text bg-gradient-to-r from-tron-primary to-tron-primary/70">Partner</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-slate-600 text-lg"
                        style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                        Verbinde dein Bankkonto sicher über Stripe, um automatisierte Provisionen zu erhalten.
                    </motion.p>
                </div>

                {/* Form Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 relative overflow-hidden shadow-xl shadow-slate-200/50"
                >
                    <form onSubmit={handleConnect} className="space-y-5">

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Deine E-Mail-Adresse
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 pl-12 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-tron-primary focus:ring-4 focus:ring-tron-primary/10 transition-all font-medium"
                                    placeholder="partner@firma.de"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="p-4 bg-red-50 border border-red-100 rounded-2xl">
                                <p className="text-sm font-medium text-red-600">{error}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || !email}
                            className="w-full relative group overflow-hidden rounded-2xl bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white font-bold px-6 py-4 flex items-center justify-center gap-2 transition-all hover:bg-tron-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-tron-primary/20"
                        >
                            {isLoading ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    <span>Verbinde mit Stripe...</span>
                                </>
                            ) : (
                                <>
                                    <span>Mit Stripe verbinden</span>
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>

                        <p className="text-xs text-center text-slate-500 mt-6 md:mt-8 px-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                            Du wirst zur sicheren Einrichtung zu Stripe weitergeleitet.
                        </p>

                    </form>
                </motion.div>

            </div>
        </div>
    );
}
